import { Dispatch, FC, MouseEvent, SetStateAction } from 'react';
import { MotionStyle } from 'motion/react';
import { ItemsAppInterface } from '../../vite-env';
import { Button } from 'primereact/button';
import { FooterStates, PassedByComplexity } from './PracticeProblemsAssessmentWrapper';
import { extractComplexity } from './PracticeProblemsAssessmentHeader';
import { ArrowRight } from 'lucide-react';
import { getPassedByLevel, getPassedReferences, isPassed } from './PracticeProblemsExperimentHelpers';
import { AchievementCriteria, PracticeProblemsAchievementTooltip } from './PracticeProblemsAchievementTooltip';
import { TAIL_TIP_FROM_RIGHT } from './PracticeProblemsAchievementTooltipTail';
import { usePracticeProblemCheckEvent } from './usePracticeProblemCheckEvent';

const SKILL_CHALLENGE_BUTTON_CLASS =
  '!tw-rounded-full !tw-border-none !tw-bg-[linear-gradient(to_right,#E07134,#B82828)] ' +
  '!tw-text-white hover:!tw-brightness-95';

// The tail hangs off the bubble's bottom-right corner, so the bubble is pushed to the right until
// that tail's tip sits over the middle of the Skill Challenge button, and lifted enough (the tail is
// ~34px tall) for it to point back down at it.
const TOOLTIP_POSITION: MotionStyle = {
  bottom: 'calc(100% + 2.5rem)',
  right: `calc(50% - ${TAIL_TIP_FROM_RIGHT}px)`,
};

interface Props {
  itemsAppRef: ItemsAppInterface;
  footerState: FooterStates;
  setFooterState: Dispatch<SetStateAction<FooterStates>>;
  enableCheck: boolean;
  setEnableCheck: Dispatch<SetStateAction<boolean>>;
  setPassedByComplexity: Dispatch<SetStateAction<PassedByComplexity>>;
  setPassedReferences: Dispatch<SetStateAction<string[]>>;
  passedByComplexity: PassedByComplexity;
  isOnLastQuestion: boolean;
  handleTakeChallenge: (e: MouseEvent) => void;
  /** Learnosity session id for this practice set attempt, reported with each "Check answer". */
  sessionId: string;
  /** The skill this practice set belongs to, reported with each "Check answer". */
  skillCode: string;
  /** Shape of `useFeatureFlagVariantKey`'s return value, read once by SkillResource. */
  experimentVariant?: string | boolean;
}

export const PracticeProblemsAssessmentFooter: FC<Props> = ({
  itemsAppRef,
  setFooterState,
  footerState,
  enableCheck,
  setEnableCheck,
  setPassedByComplexity,
  setPassedReferences,
  passedByComplexity,
  isOnLastQuestion,
  handleTakeChallenge,
  sessionId,
  skillCode,
  experimentVariant,
}) => {
  const capturePracticeProblemChecked = usePracticeProblemCheckEvent(experimentVariant);

  const checkAnswer = () => {
    itemsAppRef.getScores(scores => {
      const tags = itemsAppRef.getTags();
      const scoreByReference = itemsAppRef.getItemScores();

      const complexityByReferenceWithScore = Object.keys(tags).map(key => ({
        key,
        complexity: extractComplexity(tags[key]),
        scoring: scoreByReference[key],
      }));

      const { low, mid, high } = getPassedByLevel(complexityByReferenceWithScore);
      setPassedByComplexity({
        low: low.length,
        mid: mid.length,
        high: high.length,
      });
      // Checking the answer is what earns the check mark on the header's progress track.
      setPassedReferences(getPassedReferences(complexityByReferenceWithScore));

      // Keyed off the current item rather than `lastResponseReference` below, which is a response
      // id and not an item reference: this way the index and the correctness reported to PostHog
      // always describe the same problem, the one the student just checked.
      const currentReference = itemsAppRef.getCurrentItem().reference;
      const references = Object.keys(tags);
      const questionIndex = references.indexOf(currentReference);

      if (questionIndex !== -1) {
        capturePracticeProblemChecked({
          session_id: sessionId,
          skill_code: skillCode,
          question_index: questionIndex + 1,
          question_total: references.length,
          is_correct: isPassed(scoreByReference[currentReference]),
        });
      }

      const scoresInfo = scores;
      const responeses = itemsAppRef.getResponses();

      const lastResponseReference = Object.keys(responeses)[Object.keys(responeses).length - 1];

      const lastScore = scoresInfo[lastResponseReference];

      if (lastScore.score !== lastScore.max_score) {
        setFooterState('wrongAnswer');
      } else {
        setFooterState('correctAnswer');
      }

      itemsAppRef.validateQuestions();
    });
  };

  const tryAgain = () => {
    setFooterState('default');
    const responeses = itemsAppRef.getResponses();

    const lastResponseReference = Object.keys(responeses)[Object.keys(responeses).length - 1];
    itemsAppRef.question(lastResponseReference).resetResponse();

    setEnableCheck(false);
  };
  const nextQuestion = () => {
    setFooterState('default');
    setEnableCheck(false);
    itemsAppRef.items().next();
  };

  // One of each difficulty takes precedence over two hard ones when the student happens to meet both.
  const achievementCriteria: AchievementCriteria | null =
    passedByComplexity.low >= 1 && passedByComplexity.mid >= 1 && passedByComplexity.high >= 1
      ? 'across'
      : passedByComplexity.high >= 2
      ? 'hard'
      : null;

  const canTakeSkillChallenge = achievementCriteria !== null;

  return (
    <div className="tw-flex tw-justify-center tw-pt-4 tw-border-t tw-border-b-0 tw-border-x-0 tw-border-solid tw-border-neutral-100">
      {footerState === 'default' && (
        <Button label="Check answer" disabled={!enableCheck} onClick={checkAnswer} className="tw-mr-4" />
      )}
      {footerState === 'wrongAnswer' && (
        <div>
          <Button label="Try again" outlined onClick={tryAgain} className="tw-mr-4" />
          {!isOnLastQuestion && <Button label="Next Problem" onClick={nextQuestion} outlined className="tw-mr-4" />}
        </div>
      )}
      {footerState === 'correctAnswer' && !isOnLastQuestion && (
        <Button label="Next Problem" outlined onClick={nextQuestion} className="tw-mr-4" />
      )}
      {achievementCriteria && (
        <div className="tw-relative">
          <PracticeProblemsAchievementTooltip criteria={achievementCriteria} customStyle={TOOLTIP_POSITION} />
          <Button
            label="Take the Skill Challenge"
            onClick={handleTakeChallenge}
            className={SKILL_CHALLENGE_BUTTON_CLASS}
          />
        </div>
      )}
      {isOnLastQuestion && !canTakeSkillChallenge && (
        <Button label="Take the Skill Challenge" onClick={handleTakeChallenge}>
          <ArrowRight className="tw-ml-1" />
        </Button>
      )}
    </div>
  );
};
